/**
 * Blog Posts List - Display and manage all blog posts
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Input,
  Select,
  HStack,
  Text,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure
} from '@chakra-ui/react';
import { 
  getAllBlogPosts, 
  deleteBlogPost, 
  togglePublishBlogPost,
  getBlogCategories,
  type BlogPost,
  type BlogCategory
} from '../../services/blogService';
import { format } from 'date-fns';
import { useRef } from 'react';

interface BlogPostsListProps {
  onEdit: (postId: string) => void;
  onDelete: () => void;
  refreshTrigger: number;
}

const BlogPostsList = ({ onEdit, onDelete, refreshTrigger }: BlogPostsListProps) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'draft'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [deletePostId, setDeletePostId] = useState<string | null>(null);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();

  useEffect(() => {
    loadPosts();
    loadCategories();
  }, [refreshTrigger]);

  useEffect(() => {
    filterPosts();
  }, [posts, searchTerm, filterStatus, filterCategory]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await getAllBlogPosts();
      setPosts(data);
    } catch (error) {
      console.error('Error loading posts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load blog posts',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await getBlogCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const filterPosts = () => {
    let filtered = [...posts];

    // Filter by status
    if (filterStatus === 'published') {
      filtered = filtered.filter(p => p.isPublished);
    } else if (filterStatus === 'draft') {
      filtered = filtered.filter(p => !p.isPublished);
    }

    // Filter by category
    if (filterCategory !== 'all') {
      filtered = filtered.filter(p => p.category === filterCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.title.toLowerCase().includes(search) ||
        p.excerpt.toLowerCase().includes(search) ||
        p.tags.some(tag => tag.toLowerCase().includes(search))
      );
    }

    setFilteredPosts(filtered);
  };

  const handleTogglePublish = async (postId: string, currentStatus: boolean) => {
    try {
      await togglePublishBlogPost(postId, !currentStatus);
      toast({
        title: 'Success',
        description: `Post ${!currentStatus ? 'published' : 'unpublished'} successfully`,
        status: 'success',
        duration: 2000,
      });
      loadPosts();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update post status',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDeleteClick = (postId: string) => {
    setDeletePostId(postId);
    onOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!deletePostId) return;

    try {
      await deleteBlogPost(deletePostId);
      toast({
        title: 'Success',
        description: 'Post deleted successfully',
        status: 'success',
        duration: 2000,
      });
      onClose();
      onDelete();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete post',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'MMM d, yyyy');
  };

  if (loading) {
    return <Text>Loading posts...</Text>;
  }

  return (
    <Box>
      {/* Filters */}
      <HStack spacing={4} mb={4} flexWrap="wrap">
        <Input
          placeholder="🔍 Search posts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          maxW="300px"
        />

        <Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
          maxW="150px"
        >
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Drafts</option>
        </Select>

        <Select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          maxW="200px"
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.name}>{cat.name}</option>
          ))}
        </Select>

        <Text fontSize="sm" color="gray.600">
          Showing {filteredPosts.length} of {posts.length} posts
        </Text>
      </HStack>

      {/* Posts Table */}
      <Box
        bg="white"
        borderRadius="lg"
        boxShadow="sm"
        borderWidth="1px"
        overflowX="auto"
      >
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Title</Th>
              <Th>Category</Th>
              <Th>Status</Th>
              <Th>Views</Th>
              <Th>Published</Th>
              <Th>Updated</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredPosts.length === 0 ? (
              <Tr>
                <Td colSpan={7} textAlign="center" py={8} color="gray.500">
                  No posts found
                </Td>
              </Tr>
            ) : (
              filteredPosts.map((post) => (
                <Tr key={post.id} _hover={{ bg: 'gray.50' }}>
                  <Td maxW="300px">
                    <Text fontWeight="medium" isTruncated>
                      {post.title}
                    </Text>
                    <Text fontSize="xs" color="gray.500" isTruncated>
                      /{post.slug}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue" variant="subtle">
                      {post.category}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={post.isPublished ? 'green' : 'orange'}
                      variant="subtle"
                    >
                      {post.isPublished ? 'Published' : 'Draft'}
                    </Badge>
                  </Td>
                  <Td>{post.viewCount || 0}</Td>
                  <Td fontSize="sm">{formatDate(post.publishedAt)}</Td>
                  <Td fontSize="sm">{formatDate(post.updatedAt)}</Td>
                  <Td>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() => onEdit(post.id!)}
                      >
                        Edit
                      </Button>

                      <Menu>
                        <MenuButton
                          as={Button}
                          size="sm"
                          variant="ghost"
                        >
                          •••
                        </MenuButton>
                        <MenuList>
                          <MenuItem
                            onClick={() => handleTogglePublish(post.id!, post.isPublished)}
                          >
                            {post.isPublished ? '👁️ Unpublish' : '✅ Publish'}
                          </MenuItem>
                          <MenuItem
                            color="red.500"
                            onClick={() => handleDeleteClick(post.id!)}
                          >
                            🗑️ Delete
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Blog Post
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default BlogPostsList;
